// Adherence math + roll-up aggregations for the Nutrition Progress engine.
// Pure: operates on normalized day rows produced by dayData.buildDayRows.
// Color helpers return semantic tokens (good / warn / bad / none) — views map tokens → colors.

import moment from "moment";

import { ADHERENCE_THRESHOLDS } from "./constants";
import { MACROS } from "./dayData";

/* ---- per-day scoring ------------------------------------------------- */

// kcal derived from macros — consumed energy is never trusted from a stored
// field, always P×4 + C×4 + F×9.
export const kcalFromMacros = ({ proteins = 0, carbs = 0, fats = 0 } = {}) =>
  Math.round(proteins * 4 + carbs * 4 + fats * 9);

// Per-macro adherence as a 0–100% closeness to that macro's target.
// null when the day is unlogged or the target macro is missing / zero.
export const macroAdhPct = (day, macro) => {
  if (!day.logged || !day.target || !day.consumed) return null;
  const target = day.target[macro];
  if (!target) return null;
  return Math.max(0, 100 - (Math.abs(day.consumed[macro] - target) / target) * 100);
};

// Blended day-deviation over protein / carbs / kcal only — fat & fiber are
// intentionally excluded from the blend. 0..100+ score; null when
// unlogged or any blended target is missing.
export const dayDeviation = (day) => {
  if (!day.logged || !day.target || !day.consumed) return null;
  const t = day.target;
  if (!t.proteins || !t.carbs || !t.kcal) return null;
  const dv =
    (Math.abs(day.consumed.proteins - t.proteins) / t.proteins +
      Math.abs(day.consumed.carbs - t.carbs) / t.carbs +
      Math.abs(day.consumed.kcal - t.kcal) / t.kcal) /
    3;
  return dv * 100;
};

// Day adherence % = 100 − deviation, floored at 0. null when unlogged.
export const dayAdherence = (day) => {
  const dv = dayDeviation(day);
  return dv === null ? null : Math.max(0, Math.round(100 - dv));
};

// Color band for the day dot / chip, over the deviation score.
export const band = (day, t = ADHERENCE_THRESHOLDS) => {
  const s = dayDeviation(day);
  if (s === null) return "none";
  if (s < t.bandOnTarget) return "good";
  if (s < t.bandSlightlyOff) return "warn";
  return "bad";
};

// Per-macro cell color over |Δ|/target — intentionally stricter than the day
// band. none when there is no target to measure against.
export const macroCol = (value, target, t = ADHERENCE_THRESHOLDS) => {
  if (!target) return "none";
  const v = Math.abs(value - target) / target;
  if (v < t.macroGood) return "good";
  if (v < t.macroWarn) return "warn";
  return "bad";
};

// kcal-adherence % column color: good ≥90 · warn ≥75 · else bad.
export const adhColor = (pct, t = ADHERENCE_THRESHOLDS) => {
  if (pct === null || pct === undefined) return "none";
  if (pct >= t.adhGood) return "good";
  if (pct >= t.adhWarn) return "warn";
  return "bad";
};

/* ---- roll-up aggregations -------------------------------------------- */

// Mean of f over items, skipping items where f returns null (so a missing
// per-macro target — e.g. no fiber goal set on some days — excludes that
// item from the average instead of silently counting as a 0 contribution).
// null when nothing to average.
const meanOf = (items, f) => {
  const values = items.map(f).filter((v) => v != null);
  return values.length ? values.reduce((s, v) => s + v, 0) / values.length : null;
};

// Mean of a (possibly-missing) numeric field over the items that have it.
// Weight / steps / sleep can be absent on a day even when intake is logged.
const meanPresent = (items, key) =>
  meanOf(
    items.filter((d) => d[key] != null),
    (d) => d[key]
  );

const roundOrNull = (v) => (v === null ? null : Math.round(v));

// Average row over a set of day rows.
// n = days with nutrition logged. Adherence / macro averages are over the
// subset that also has a plan target; n === 0 → "no logged days".
export const avgOf = (rows) => {
  const total = rows.length;
  const logged = rows.filter((d) => d.logged);
  const withConsumed = logged.filter((d) => d.consumed);
  const withTarget   = logged.filter((d) => d.consumed && d.target);

  if (!logged.length) return { n: 0, total };

  const consumed = {};
  const target   = {};
  const macroAdh = {};
  const avgDelta = {};

  if (withConsumed.length) {
    MACROS.forEach((m) => {
      consumed[m] = roundOrNull(meanOf(withConsumed, (d) => d.consumed[m]));
    });
  }
  if (withTarget.length) {
    MACROS.forEach((m) => {
      target[m]   = roundOrNull(meanOf(withTarget, (d) => d.target[m]));
      macroAdh[m] = roundOrNull(meanOf(withTarget, (d) => macroAdhPct(d, m)));
      // Mean of each day's OWN (consumed - target) delta — NOT
      // avg.consumed[m] - avg.target[m]. Those 2 fields are means over
      // different subsets (consumed over withConsumed, target/macroAdh
      // over withTarget, which is consumed-AND-has-a-target) — subtracting
      // them would only equal the true average delta when those subsets
      // happen to coincide. Computed directly over withTarget instead, the
      // same subset macroAdh already uses, so the 2 stay consistent.
      //
      // Each day's own consumed/target are rounded BEFORE differencing —
      // mirrors DayRow's per-day cell (TableRows.js), which does the same
      // for the same reason: a raw-decimal delta can land 1 off from what
      // simple subtraction of the two ALREADY-ROUNDED numbers a coach sees
      // elsewhere in the row would give. Still averaged over withTarget
      // (not re-derived from the rounded consumed[m]/target[m] AVERAGES
      // above, which would reintroduce the denominator mismatch this exact
      // comment already warns against).
      avgDelta[m] = roundOrNull(
        meanOf(withTarget, (d) => Math.round(d.consumed[m]) - Math.round(d.target[m]))
      );
    });
  }

  return {
    n: logged.length,
    total,
    consumed:  withConsumed.length ? consumed  : null,
    target:    withTarget.length   ? target    : null,
    macroAdh:  withTarget.length   ? macroAdh  : null,
    avgDelta:  withTarget.length   ? avgDelta  : null,
    adh: withTarget.length ? roundOrNull(meanOf(withTarget, (d) => dayAdherence(d))) : null,
    weight: meanPresent(logged, "weight"),
    steps:  roundOrNull(meanPresent(logged, "steps")),
    sleep:  roundOrNull(meanPresent(logged, "sleep")),
    hrv: roundOrNull(meanPresent(logged, "hrv")),
    restingHeartRate: roundOrNull(meanPresent(logged, "restingHeartRate")),
    readinessScore: roundOrNull(meanPresent(logged, "readinessScore")),
    sessionRPE: meanPresent(logged, "sessionRPE"),
    waterIntake: meanPresent(logged, "waterIntake"),
    bodyFatPercentage: meanPresent(logged, "bodyFatPercentage"),
  };
};

// Group day rows into ISO calendar weeks (Mon–Sun), newest-bucket-first with
// each bucket newest-day-first. A week straddling two plans still groups by
// calendar week.
export const weekGroups = (rows) => {
  const byWeek = new Map(); // mondayUtc → rows[]
  rows.forEach((row) => {
    const monday = Number(
      moment(String(row.utcDate), "YYYYMMDD").startOf("isoWeek").format("YYYYMMDD")
    );
    if (!byWeek.has(monday)) byWeek.set(monday, []);
    byWeek.get(monday).push(row);
  });

  return [...byWeek.entries()]
    .sort((a, b) => b[0] - a[0])
    .map(([, weekRows]) => weekRows.sort((a, b) => b.utcDate - a.utcDate));
};

// Group day rows by ISO weekday into a Mon→Sun array (index 0 = Mon … 6 = Sun).
// Used by the Calendar bottom row and reused by the Graphs weekday chart.
export const weekdayGroups = (rows) => {
  const byWeekday = Array.from({ length: 7 }, () => []);
  rows.forEach((row) => {
    const isoWeekday = moment(String(row.utcDate), "YYYYMMDD").isoWeekday(); // 1..7
    byWeekday[isoWeekday - 1].push(row);
  });
  return byWeekday;
};
